import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Shield, Users, Award, Heart, Target, Zap, CheckCircle } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const About = () => {
  const values = [
    {
      icon: Shield,
      title: "Transparency First",
      description: "No hidden fees, no pressure tactics. We believe in honest, upfront pricing and clear communication."
    },
    {
      icon: Users,
      title: "Customer-Centric",
      description: "Your needs come first. We match you with carriers based on what's best for you, not what pays us the most."
    },
    {
      icon: Zap,
      title: "Technology-Driven",
      description: "Our AI-powered matching system finds the perfect carrier for your unique situation in minutes."
    },
    {
      icon: Heart,
      title: "Family-Focused",
      description: "We understand that life insurance is about protecting the people you love most."
    }
  ];

  const stats = [
    { number: "50,000+", label: "Customers Served" },
    { number: "15+", label: "Carrier Partners" },
    { number: "2 minutes", label: "Average Quote Time" },
    { number: "98%", label: "Customer Satisfaction" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-primary-light/30 to-background">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6">
              About Safurra
            </h1>
            <h2 className="text-2xl md:text-3xl text-primary mb-8 font-semibold">
              The Smarter Way to Get Life Insurance
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed">
              We're revolutionizing life insurance by combining cutting-edge technology with 
              personalized service. No more pushy agents, confusing policies, or endless paperwork. 
              Just honest, transparent coverage that protects what matters most.
            </p>
            <Link to="/">
              <Button size="lg" variant="hero">
                Get Your Quote Now
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 id="mission" className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                Our Mission
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                To make life insurance simple, accessible, and transparent for everyone.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-2xl font-semibold text-foreground mb-6">
                  Why Safurra Exists
                </h3>
                <div className="space-y-4 text-muted-foreground">
                  <p>
                    Traditional life insurance shopping is broken. It's complicated, time-consuming, 
                    and often feels like you're being sold something you don't need.
                  </p>
                  <p>
                    We created Safurra to fix that. Our AI-powered platform analyzes your unique 
                    situation and matches you with the right carrier and policy - all without 
                    the pressure and confusion of traditional insurance sales.
                  </p>
                  <p>
                    Every recommendation is based on what's truly best for you and your family, 
                    not what generates the highest commission.
                  </p>
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="bg-primary/5 rounded-2xl p-6 border border-primary/20">
                  <Target className="h-8 w-8 text-primary mb-4" />
                  <h4 className="font-semibold text-foreground mb-2">Our Goal</h4>
                  <p className="text-muted-foreground text-sm">
                    Make life insurance accessible to every family, regardless of income or health status.
                  </p>
                </div>
                
                <div className="bg-accent/30 rounded-2xl p-6">
                  <Award className="h-8 w-8 text-primary mb-4" />
                  <h4 className="font-semibold text-foreground mb-2">Our Promise</h4>
                  <p className="text-muted-foreground text-sm">
                    Always recommend what's best for you, provide transparent pricing, and respect your time.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-primary-light/30">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                What Makes Us Different
              </h2>
              <p className="text-xl text-muted-foreground">
                Our core values guide every decision we make and every recommendation we provide.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <Card key={index} className="text-center p-8 hover:shadow-lg transition-shadow">
                  <CardContent className="p-0">
                    <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <value.icon className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-3">
                      {value.title}
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                Trusted by Families Nationwide
              </h2>
              <p className="text-xl text-muted-foreground">
                Join thousands of families who've found the right coverage through Safurra.
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              {stats.map((stat, index) => (
                <div key={index} className="space-y-2">
                  <div className="text-3xl md:text-4xl font-bold text-primary">
                    {stat.number}
                  </div>
                  <div className="text-muted-foreground">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-primary-light/30">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
              Built by Insurance & Technology Experts
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Our team combines decades of insurance industry experience with cutting-edge 
              technology expertise to create the best possible experience for our customers.
            </p>
            
            <div className="grid md:grid-cols-3 gap-8 text-sm text-muted-foreground">
              <div className="space-y-2">
                <CheckCircle className="h-8 w-8 text-primary mx-auto mb-4" />
                <h4 className="font-semibold text-foreground">Licensed Professionals</h4>
                <p>All recommendations reviewed by licensed insurance professionals</p>
              </div>
              <div className="space-y-2">
                <CheckCircle className="h-8 w-8 text-primary mx-auto mb-4" />
                <h4 className="font-semibold text-foreground">Technology First</h4>
                <p>AI-powered matching ensures you get the most relevant options</p>
              </div>
              <div className="space-y-2">
                <CheckCircle className="h-8 w-8 text-primary mx-auto mb-4" />
                <h4 className="font-semibold text-foreground">Customer Support</h4>
                <p>Real people available to help when you need assistance</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
              Ready to Experience the Difference?
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Join thousands of families who've found better coverage through Safurra.
            </p>
            <Link to="/">
              <Button size="lg" variant="hero">
                Get Your Free Quote
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;